import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, CheckCircle, X } from "lucide-react";

interface SummaryBlockProps {
  summary?: string;
  bullets?: string[];
  defaultOpen?: boolean;
  className?: string;
  showSummary?: boolean;
}

export function SummaryBlock({ 
  summary, 
  bullets, 
  defaultOpen = false, 
  className = '',
  showSummary = true
}: SummaryBlockProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  // Handle hash #resume in URL
  useEffect(() => {
    if (window.location.hash === '#resume') {
      setIsOpen(true);
    }
  }, []);

  // Don't render if no content or disabled
  if (!showSummary || (!summary && (!bullets || bullets.length === 0))) {
    return null;
  }

  const toggleOpen = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    
    // Update URL hash
    if (newState) {
      window.history.pushState(null, '', '#resume');
    } else {
      window.history.pushState(null, '', window.location.pathname);
    }
  };

  // Handle Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
        window.history.pushState(null, '', window.location.pathname);
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  return (
    <>
      {/* Chip Trigger - Mobile: Sticky bottom-right, Desktop: Inline */}
      <div className={`${className} flex justify-end mb-6`}>
        <Button
          variant="outline"
          size="sm"
          className="fixed md:relative bottom-4 right-4 md:bottom-auto md:right-auto z-40 bg-background/95 backdrop-blur-sm border-primary/30 hover:bg-primary/10 shadow-lg md:shadow-none"
          onClick={toggleOpen}
          aria-expanded={isOpen}
          aria-controls="summary-drawer"
          role="button"
        >
          <Clock className="h-4 w-4 mr-2" />
          Résumé (30 s)
        </Button>
      </div>

      {/* Drawer */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-black/20 backdrop-blur-sm md:relative md:inset-auto md:bg-transparent md:backdrop-blur-none md:p-0"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsOpen(false);
              window.history.pushState(null, '', window.location.pathname);
            }
          }}
        >
          <Card 
            id="summary-drawer"
            className="w-full max-w-2xl mt-20 md:mt-0 bg-muted/30 border-primary/20 animate-in slide-in-from-top-4 duration-300"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-primary" />
                  <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">
                    Résumé (30 s)
                  </Badge>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 md:hidden"
                  onClick={() => {
                    setIsOpen(false);
                    window.history.pushState(null, '', window.location.pathname);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              
              {summary && (
                <p className="text-muted-foreground mb-4 leading-relaxed">
                  {summary}
                </p>
              )}
              
              {bullets && bullets.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3 text-foreground">Points clés :</h3>
                  <ul className="space-y-2">
                    {bullets.map((bullet, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Print styles - handled via global CSS */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @media print {
            .fixed { position: static !important; }
            #summary-drawer { 
              display: block !important;
              position: static !important;
              margin: 1rem 0;
            }
          }
        `
      }} />
    </>
  );
}